import React, { useState } from 'react';
import { getMultipliedIndentFromProp, getTypographyFontStyle } from 'theme/selectors';
import { FontSize } from 'theme';
import SortIcon from '../Icon/SortIcon';
import { addStyleIfPropTruly } from 'utils/styledComponents';
import { colors } from 'constants/colors';
import { directSortStates, WLabel } from 'constants/enums';
import { mediaSmDown } from 'theme/helpers/css';
import styled from 'styled-components';
import { selectByWL } from 'utils/wLabel';
import Text from 'Components/Typography/Text';

export interface ITab {
  type: string;
  title: string;
  onSort: (type: string, direction?: string) => void;
  hasDirection?: boolean;
}

interface ITabStyled {
  active?: boolean;
  direction: directSortStates;
}

interface ISortingTab extends ITab, Omit<ITabStyled, 'direction'> {
  onClick: () => void;
  isCheap?: boolean;
}

const wlTabStyles = selectByWL({
  [WLabel.Default]: 'padding-bottom: 12px;',
  [WLabel.Globus]: 'padding-bottom: 16px;',
});

const Tab = styled.div<ITabStyled>`
  ${getTypographyFontStyle(FontSize.s)};
  color: ${colors.warmGrey};
  margin: 0 ${getMultipliedIndentFromProp(4)};
  position: relative;
  user-select: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  ${wlTabStyles}

  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
  }

  &:after {
    content: '';
    height: 1px;
    background-color: ${colors.darkGrey};
    position: absolute;
    opacity: 0;
    width: 100%;
    left: 0;
    bottom: 0;
  }

  ${mediaSmDown`
    padding-bottom: 8px;
    flex-shrink: 0;

    ${getTypographyFontStyle(FontSize.xs)}
  `}

  ${addStyleIfPropTruly(
    'active',
    `
    color: ${colors.darkGrey};
    &:after { opacity: 1; }
    `
  )}
`;

const ProductChip = styled.button<{ isChipOnFocus?: boolean }>`
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  margin: 0 5px 8px 0;
  padding: 0 16px 1px;
  width: fit-content;
  height: 36px;
  background-color: ${colors.white};
  border: 1px solid ${colors.lightGreyThree};
  border-radius: 100px;
  cursor: pointer;
  ${({ isChipOnFocus }) =>
    isChipOnFocus &&
    `
      border: 1px solid ${colors.mintHaze};
      background-color: ${colors.mintHaze};
    `}
`;

const ChipName = styled(Text).attrs({ color: colors.blackRussian })<{ isShowMore?: boolean }>`
  letter-spacing: -0.2px;
  font-size: 13px;
  ${({ isShowMore }) =>
    isShowMore &&
    `
      color: ${colors.paleGreen};
    `}
`;

const StyledSortIcon = styled(SortIcon)<ITabStyled>`
  margin-left: 4px;
  opacity: 0;

  ${addStyleIfPropTruly(
    'active',
    `
    opacity: 1;
    `
  )}

  ${({ direction }) =>
    direction === directSortStates.desc &&
    `
      transform: scaleY(-1);
    `}
`;

const SortingTab: React.FC<ISortingTab> = ({ title, type, hasDirection, active, onSort, onClick, isCheap }) => {
  const [direction, setDirection] = useState(directSortStates.asc);
  const getNextDirection = () => {
    if (active) {
      if (direction === directSortStates.desc) {
        return directSortStates.asc;
      }

      return directSortStates.desc;
    }

    return direction;
  };

  const onClickHandler = () => {
    const nextDirection = getNextDirection();
    if (onClick) onClick();
    setDirection(nextDirection);
    if (hasDirection) {
      onSort(type, nextDirection);
    } else {
      onSort(type);
    }
  };

  return isCheap ? (
    <ProductChip isChipOnFocus={active} onClick={onClickHandler}>
      <ChipName>{title}</ChipName>
    </ProductChip>
  ) : (
    <Tab key={type} active={active} onClick={onClickHandler} data-testid={`filter-sort-${type}`} direction={direction}>
      {title}
      {hasDirection && <StyledSortIcon active={active} direction={direction} />}
    </Tab>
  );
};

export default SortingTab;
